import axiosInstance from '@/plugins/axios';

/**
 * The public Quran API (no account). Text never changes between deploys, so
 * surahs are memoised for the lifetime of the page; searches are not.
 */
const memo = new Map();

function remember(key, load) {
  if (!memo.has(key)) {
    memo.set(key, load().catch((error) => {
      memo.delete(key);
      throw error;
    }));
  }
  return memo.get(key);
}

const get = (url, params) => axiosInstance.get(url, { params }).then((r) => r.data);

export const fetchSurahs = () => remember('surahs', () => get('/api/quran/surahs').then((r) => r.data));

export const fetchSurah = (surah) => remember(`surah:${surah}`, () => get(`/api/quran/surahs/${surah}`).then((r) => r.data));

export const fetchRelated = (surah, ayah) =>
  remember(`related:${surah}:${ayah}`, () => get(`/api/quran/surahs/${surah}/ayahs/${ayah}/related`).then((r) => r.data));

/**
 * Word by word: [{ position, arabic, transliteration, meaning: { bs, en },
 * root, lemma, pos, segments, tokens }] — tokens are the word's indexes in
 * the ayah's Arabic text split on spaces (what ArabicText reports on a tap).
 * `v` changes when the meanings are regenerated, so browsers holding an older
 * answer fetch the new one instead of waiting for their cache to expire.
 */
const WORDS_VERSION = 3;
export const fetchAyahWords = (surah, ayah) =>
  remember(`words-of:${surah}:${ayah}`, () =>
    get(`/api/quran/surahs/${surah}/ayahs/${ayah}/words`, { v: WORDS_VERSION }).then((r) => r.data)
  );

/** Keyword search: { data, surahs, jump, meta } */
export const searchAyahs = ({ q, lang, surah, page = 1 }) => get('/api/quran/ayahs', { q, lang, surah, page });

/** Word statistics: occurrences, spread over surahs, and a page of ayat. */
export const fetchWordStats = ({ q, lang, mode, page = 1 }) =>
  remember(`words:${lang}:${mode}:${q}:${page}`, () => get('/api/quran/words', { q, lang, mode, page }).then((r) => r.data));

export const searchByMeaning = (q) => get('/api/quran/meaning', { q }).then((r) => r.data);

/** A surah's tafsir in one language: { data: [{ ayah, key, text }], meta: { source } } */
export const fetchSurahTafsir = (surah, lang) =>
  remember(`tafsir:${lang}:${surah}`, () => get(`/api/quran/surahs/${surah}/tafsir`, { lang }));

/** One ayah's tafsir in every language: { bs: { text, source }, en: …, ar: … } */
export const fetchAyahTafsir = (surah, ayah) =>
  remember(`tafsir-of:${surah}:${ayah}`, () => get(`/api/quran/surahs/${surah}/ayahs/${ayah}/tafsir`).then((r) => r.data));

/** Full-text search through the tafsir: { data: [{ key, snippet, marks, ayah_text }], meta } */
export const searchTafsir = ({ q, lang, surah, page = 1 }) => get('/api/quran/tafsir', { q, lang, surah, page });

/** What the scholars said, by words and by meaning: { data: [{ id, lang, title, excerpt, scholars, url, source }] } */
export const searchOpinions = ({ q, lang }) => get('/api/quran/opinions', { q, lang });

/** One answer in full, with the ayat and hadiths it cites: { data } */
export const fetchOpinion = (id) => get(`/api/quran/opinions/${id}`);

/** The answers that cite an ayah */
export const fetchAyahOpinions = (surah, ayah, lang) =>
  get(`/api/quran/surahs/${surah}/ayahs/${ayah}/opinions`, { lang }).then((r) => r.data);

/** Every root with its words and meanings, most frequent first: { data, meta: { source } } */
export const fetchRoots = () => remember('roots', () => get('/api/quran/roots'));

/** One root through the Quran — its words, spread over surahs, a page of ayat. `lemma` narrows it to one word. */
export const fetchRoot = ({ root, lemma, page = 1 }) =>
  remember(`root:${root}:${lemma ?? ''}:${page}`, () => get(`/api/quran/roots/${encodeURIComponent(root)}`, { lemma, page }));

/** One ayah in every translation: [{ id, lang, text, footnotes, source }] */
export const fetchAyahTranslations = (surah, ayah) =>
  remember(`translations-of:${surah}:${ayah}`, () => get(`/api/quran/surahs/${surah}/ayahs/${ayah}/translations`).then((r) => r.data));

/** A surah in extra translations: { [id]: { source, ayahs: { [number]: { text, footnotes } } } } — one request per translation, each memoised. */
export const fetchSurahTranslations = (surah, ids) =>
  Promise.all(
    ids.map((id) =>
      remember(`translation:${id}:${surah}`, () => get(`/api/quran/surahs/${surah}/translations`, { editions: id }).then((r) => r.data[id]))
    )
  ).then((parts) => Object.fromEntries(ids.map((id, i) => [id, parts[i]])));

/** The order of revelation and the Meccan / Medinan comparison. */
export const fetchChronology = () => remember('chronology', () => get('/api/quran/chronology').then((r) => r.data));
